"use client";

const DB_NAME = "fexr-offline";
const DB_VERSION = 1;
const STORE_NAME = "records";
const MAX_RECORDS = 100;
const DEFAULT_SNAPSHOT_TTL = 24 * 60 * 60 * 1000;
const DEFAULT_DRAFT_TTL = 7 * 24 * 60 * 60 * 1000;

type OfflineRecord<T = unknown> = {
  id: string;
  kind: "snapshot" | "draft";
  scope: string;
  value: T;
  savedAt: number;
  expiresAt: number;
};

export type OfflineValue<T> = {
  value: T;
  savedAt: number;
};

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is unavailable"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => Promise<T>,
) {
  const database = await openDatabase();
  try {
    const store = database
      .transaction(STORE_NAME, mode)
      .objectStore(STORE_NAME);
    return await operation(store);
  } finally {
    database.close();
  }
}

function recordId(kind: OfflineRecord["kind"], scope: string, key: string) {
  return `${kind}:${scope}:${key}`;
}

async function prune() {
  try {
    await withStore("readwrite", async (store) => {
      const records = (await requestValue(store.getAll())) as OfflineRecord[];
      const now = Date.now();
      const sorted = [...records].sort((a, b) => b.savedAt - a.savedAt);
      const remove = sorted.filter(
        (record, index) => record.expiresAt <= now || index >= MAX_RECORDS,
      );
      await Promise.all(
        remove.map((record) => requestValue(store.delete(record.id))),
      );
    });
  } catch {
    // A quota/private-mode failure must not prevent the live app from working.
  }
}

async function putRecord<T>(record: OfflineRecord<T>) {
  await withStore("readwrite", async (store) => {
    await requestValue(store.put(record));
  });
  void prune();
}

async function getRecord<T>(id: string): Promise<OfflineValue<T> | null> {
  try {
    return await withStore("readwrite", async (store) => {
      const record = (await requestValue(store.get(id))) as
        | OfflineRecord<T>
        | undefined;
      if (!record) return null;
      if (record.expiresAt <= Date.now()) {
        await requestValue(store.delete(id));
        return null;
      }
      return { value: record.value, savedAt: record.savedAt };
    });
  } catch {
    return null;
  }
}

export async function putOfflineSnapshot<T>(
  key: string,
  value: T,
  options: { scope?: string; ttlMs?: number } = {},
) {
  const savedAt = Date.now();
  const scope = options.scope || "public";
  try {
    await putRecord({
      id: recordId("snapshot", scope, key),
      kind: "snapshot",
      scope,
      value,
      savedAt,
      expiresAt: savedAt + (options.ttlMs || DEFAULT_SNAPSHOT_TTL),
    });
    return true;
  } catch {
    return false;
  }
}

export function getOfflineSnapshot<T>(key: string, scope = "public") {
  return getRecord<T>(recordId("snapshot", scope, key));
}

export async function putOfflineDraft<T>(
  key: string,
  value: T,
  options: { scope?: string; ttlMs?: number } = {},
) {
  const savedAt = Date.now();
  const scope = options.scope || "local";
  try {
    await putRecord({
      id: recordId("draft", scope, key),
      kind: "draft",
      scope,
      value,
      savedAt,
      expiresAt: savedAt + (options.ttlMs || DEFAULT_DRAFT_TTL),
    });
    return true;
  } catch {
    return false;
  }
}

export function getOfflineDraft<T>(key: string, scope = "local") {
  return getRecord<T>(recordId("draft", scope, key));
}

export async function deleteOfflineDraft(key: string, scope = "local") {
  try {
    await withStore("readwrite", async (store) => {
      await requestValue(store.delete(recordId("draft", scope, key)));
    });
  } catch {
    // Best-effort cleanup.
  }
}

export async function clearAllOfflineData() {
  if (typeof indexedDB === "undefined") return;
  await new Promise<void>((resolve) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
}
